import { useMutation } from "@apollo/client";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import { Alert, Box, CircularProgress, GridLegacy as Grid, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FormTextField } from "components/Forms/FormTextField";
import { gql } from "generated-graphql";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { useReport } from "../useReport";

export const UPDATE_NOTES_TO_REGULATOR = gql(`
  mutation UpdateNotesToRegulator($reportId: ID!, $notes: String) {
    updateNotesToRegulators(id: $reportId, notes: $notes) {
      id
      organizationStatus
      notesToRegulators
      touchedSteps
      assignees {
        id
        isComplete
      }
    }
  }
`);

type FormState = {
  notesToRegulators?: string | null;
};

export function NotesToRegulatorsForm() {
  const theme = useTheme();
  const alerts = useAlerts();
  const { reportId = "" } = useParams<{ reportId: string }>();
  const { data } = useReport();
  const [saveSuccessful, setSaveSuccessful] = useState(false);

  const [updateNotesToRegulators, { loading: updatingNotes }] =
    useMutation(UPDATE_NOTES_TO_REGULATOR);

  const originalNotes: FormState = useMemo(
    () => ({
      notesToRegulators: data?.tierIIReport.notesToRegulators,
    }),
    [data?.tierIIReport.notesToRegulators]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitSuccessful },
  } = useForm<FormState>({
    defaultValues: originalNotes,
  });

  useEffect(() => {
    reset(originalNotes);
  }, [originalNotes, reset]);

  const onNotesUpdated = handleSubmit(async (data) => {
    await updateNotesToRegulators({
      variables: {
        reportId,
        notes: data.notesToRegulators,
      },
    });
    setSaveSuccessful(true);
    setTimeout(() => setSaveSuccessful(false), 3000); // hide the success icon
  });

  useEffect(() => {
    if (!isSubmitSuccessful) return;
    alerts.success("Successfully updated Notes to Regulators");
  }, [alerts, isSubmitSuccessful]);

  return (
    <>
      <Grid
        container
        spacing={1}
        marginTop={theme.spacing(3)}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "100%",
        }}
      >
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mt: -2, mb: 1 }}>
            Enter any notes or messages here that you would like to communicate directly to the
            regulator reviewing this report. This message will not be seen by Encamp or your
            colleagues.
          </Alert>
          <FormTextField
            name="notesToRegulators"
            fullWidth
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                Notes to Regulators
                {updatingNotes ? <CircularProgress size={16} style={{ marginLeft: 10 }} /> : null}
                {saveSuccessful && !updatingNotes ? (
                  <CheckCircleOutline color="success" fontSize="small" style={{ marginLeft: 10 }} />
                ) : null}
              </Box>
            }
            control={control}
            onBlur={onNotesUpdated}
            textFieldProps={{
              minRows: 6,
              multiline: true,
              variant: "outlined",
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}
